import { Injectable } from '@angular/core';
import {
  MonthlyStatsPerformanceGQL,
  MonthlyStatsPerformanceQuery,
  MonthlyStatsTargetsGQL,
  MonthlyStatsTargetsQuery,
  MyMonthlyStatsGQL,
  SaveMonthlyStatsTargetsGQL,
  SaveMonthlyStatsTargetsInput,
} from '@app/generated/graphql';
import { ApolloQueryResult } from '@apollo/client/core';
import { CognitoAuthService } from '@auth/services/cognito-auth.service';
import { Observable, catchError, forkJoin, map, of, shareReplay, switchMap } from 'rxjs';
import { AccessibleAgentsService } from '@app/shared/services/accessible-agents/accessible-agents.service';
import {
  MonthTargets,
  MonthlyStatsAccess,
  MonthlyStatsView,
  PerformanceGrid,
} from '../models/my-monthly-stats.model';
import { toMonthlyStatsView } from '../models/monthly-stats.view';

const SILENT_QUERY = {
  errorPolicy: 'all' as const,
  context: { skipGraphqlErrorUi: true },
};

@Injectable({
  providedIn: 'root',
})
export class MyMonthlyStatsService {
  access$?: Observable<MonthlyStatsAccess>;

  constructor(
    private myMonthlyStatsGQL: MyMonthlyStatsGQL,
    private monthlyStatsPerformanceGQL: MonthlyStatsPerformanceGQL,
    private monthlyStatsTargetsGQL: MonthlyStatsTargetsGQL,
    private saveMonthlyStatsTargetsGQL: SaveMonthlyStatsTargetsGQL,
    private accessibleAgentsService: AccessibleAgentsService,
    private authService: CognitoAuthService,
  ) {}

  getAccess() {
    if (!this.access$) {
      this.access$ = forkJoin({
        stats: this.myMonthlyStatsGQL.fetch(undefined, SILENT_QUERY).pipe(
          map((result) => {
            if (hasGraphQlErrors(result) || !result.data?.myMonthlyStats) {
              return null;
            }

            return toMonthlyStatsView(result.data.myMonthlyStats);
          }),
          catchError(() => of(null)),
        ),
        isTeamLead: this.accessibleAgentsService
          .getAccessibleAgents()
          .pipe(map((agents) => agents.length > 1)),
      }).pipe(
        map(({ stats, isTeamLead }) => ({
          canView: stats != null,
          isTeamLead: stats != null && isTeamLead,
          stats,
        })),
        shareReplay(1),
      );
    }

    return this.access$;
  }

  getMyMonthlyStats() {
    return this.getAccess().pipe(map((access) => access.stats));
  }

  getPerformanceGrid() {
    return this.getAccess().pipe(
      switchMap((access) =>
        access.isTeamLead
          ? this.fetchTeamPerformanceGrid()
          : of(toSelfPerformanceGrid(access.stats, this.authService.user)),
      ),
    );
  }

  fetchTeamPerformanceGrid() {
    return this.monthlyStatsPerformanceGQL
      .fetch()
      .pipe(
        map((result) =>
          hasGraphQlErrors(result) ? null : toPerformanceGrid(result.data?.monthlyStatsPerformance),
        ),
      );
  }

  getMonthTargets(month: string) {
    return this.monthlyStatsTargetsGQL.fetch({ month }).pipe(
      map(({ data }) => toMonthTargets(data?.monthlyStatsTargets, month)),
      switchMap((targets) => this.withRosterIfEmpty(targets)),
    );
  }

  withRosterIfEmpty(targets: MonthTargets) {
    if (targets.agents.length) {
      return of(targets);
    }

    return this.getPerformanceGrid().pipe(
      map((performance) => ({
        ...targets,
        agents: rosterFromPerformance(performance),
      })),
    );
  }

  saveMonthTargets(targets: MonthTargets) {
    return this.saveMonthlyStatsTargetsGQL
      .mutate({
        input: {
          month: targets.month,
          team: {
            gpTarget: targets.team.gpTarget,
            nlcr: targets.team.nlcr,
          },
          agents: targets.agents.map((agent) => ({
            agentId: agent.agentId,
            gpTarget: agent.gpTarget,
            nlcr: agent.nlcr,
            ancillaryAttachRate: agent.ancillaryAttachRate,
          })),
        } as SaveMonthlyStatsTargetsInput,
      })
      .pipe(map(({ data }) => toMonthTargets(data?.saveMonthlyStatsTargets, targets.month)));
  }
}

function hasGraphQlErrors(result: ApolloQueryResult<unknown>) {
  return Boolean(result.errors?.length);
}

function rosterFromPerformance(performance: PerformanceGrid | null) {
  return (performance?.agents ?? []).map((agent) => ({
    agentId: agent.id,
    fullName: agent.fullName,
    picture: agent.picture ?? '',
    gpTarget: null,
    nlcr: null,
    ancillaryAttachRate: null,
  }));
}

function toSelfPerformanceGrid(
  stats: MonthlyStatsView | null,
  user: { id: string; fullName?: string; picture?: string },
): PerformanceGrid | null {
  if (!stats || !user.id) {
    return null;
  }

  return {
    lastSyncedAt: stats.lastSyncedAt,
    agents: [
      {
        id: user.id,
        fullName: user.fullName ?? '',
        picture: user.picture,
        stats,
      },
    ],
    totals: {
      ...stats,
      workingDays: null,
      leads: { ...stats.leads, baselineNlcr: null },
      deals: { ...stats.deals, baselineAncillaryAttachRate: null },
      nlcrMeetsBaseline: null,
      ancillaryMeetsBaseline: null,
    },
  };
}

function toPerformanceGrid(
  node: MonthlyStatsPerformanceQuery['monthlyStatsPerformance'] | null | undefined,
): PerformanceGrid | null {
  if (!node) {
    return null;
  }

  return {
    lastSyncedAt: node.lastSyncedAt ?? undefined,
    agents: node.agents.map((agent) => ({
      id: agent.id,
      fullName: agent.fullName,
      picture: agent.picture ?? undefined,
      stats: toMonthlyStatsView(agent.stats),
    })),
    totals: toMonthlyStatsView(node.totals),
  };
}

function toMonthTargets(
  node: MonthlyStatsTargetsQuery['monthlyStatsTargets'] | null | undefined,
  month: string,
): MonthTargets {
  if (!node) {
    return {
      month,
      team: { gpTarget: null, nlcr: null },
      agents: [],
    };
  }

  return {
    month: node.month,
    team: {
      gpTarget: node.team.gpTarget ?? null,
      nlcr: node.team.nlcr ?? null,
    },
    agents: node.agents.map((agent) => ({
      agentId: agent.agentId,
      fullName: agent.fullName,
      picture: agent.picture ?? '',
      gpTarget: agent.gpTarget ?? null,
      nlcr: agent.nlcr ?? null,
      ancillaryAttachRate: agent.ancillaryAttachRate ?? null,
    })),
  };
}
